// src/pages/auth/ResetPassword.jsx
import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import withRouter from "../../components/Common/withRouter";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useResetPassword } from "../../api/auth"; // make sure this hook exists
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Form,
  Label,
  Input,
  FormFeedback,
  Button,
  Alert
} from "reactstrap";
import { createSearchParams } from "react-router-dom";
import CustomHeader from "../../components/Auth/CustomHeader";

function ResetPassword(props) {
  const { t } = props;

  // read query params (?email=...&code=...)
  const search = props?.router?.location?.search || "";
  const qp = useMemo(() => new URLSearchParams(search), [search]);
  const email = qp.get("email") || "";
  const code = qp.get("code") || "";

  const { mutate, isPending, isError, error, isSuccess } = useResetPassword();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: "",
      confirmPassword: ""
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, t("resetPasswordMinChars"))
        .required(t("resetPasswordRequired")),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], t("resetPasswordMustMatch"))
        .required(t("resetPasswordConfirmRequired"))
    }),
    onSubmit: (values) => {
      mutate(
        {
          email,
          code,
          password: values.password,
          confirmPassword: values.confirmPassword
        },
        {
          onSuccess: () => {
            // go to login with a friendly flag
            const search = createSearchParams({ reset: "success" }).toString();
            props.router.navigate({ pathname: "/login", search: `?${search}` });
          }
        }
      );
    }
  });

  return (
    <>
      <CustomHeader />
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card>
                <CardBody className="p-4">
                  <h4 className="mb-2">{t("resetPasswordHeading")}</h4>
                  <p className="text-muted mb-4">{t("resetPasswordSubheading", { email })}</p>

                  {isError && (
                    <Alert color="danger" className="mb-3">
                      {error?.message || t("resetPasswordGenericError")}
                    </Alert>
                  )}
                  {isSuccess && (
                    <Alert color="success" className="mb-3">
                      {t("resetPasswordSuccess")}
                    </Alert>
                  )}

                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      formik.handleSubmit();
                      return false;
                    }}
                  >
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="password">
                        {t("resetPasswordNewPassword")}
                      </Label>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.password && !!formik.errors.password}
                      />
                      <FormFeedback>{formik.errors.password}</FormFeedback>
                    </div>

                    <div className="mb-3">
                      <Label className="form-label" htmlFor="confirmPassword">
                        {t("resetPasswordConfirmPassword")}
                      </Label>
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        value={formik.values.confirmPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
                      />
                      <FormFeedback>{formik.errors.confirmPassword}</FormFeedback>
                    </div>

                    <Button
                      type="submit"
                      color="secondary"
                      className="w-100"
                      disabled={isPending || !formik.isValid}
                    >
                      {isPending ? t("resetPasswordSubmitting") : t("resetPasswordSubmit")}
                    </Button>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
}

export default withRouter(withTranslation()(ResetPassword));
